import LogRocket from "logrocket"
import { useEffect } from "react"
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
  Outlet,
} from "react-router-dom"
import { useTypedSelector } from "./app/store"
import { useSessionCheck } from "./hooks/useSessionCheck"

// Import for side-effects only - registers global console utilities
import "./utils/sessionUtils"

import { RecruiterAdminRoutes, SiteAdminRoutes, TeamMemberRoutes } from "./components/RouteGroups"
import TutorialBase from "./features/tutorial/components/tutorial-base"
import ForgotPassword from "./pages/forgot-password"
import LogIn from "./pages/login-plain"
import RecruiterAdminDashboard from "./pages/recruiter-admin-dashboard"
import ResetPassword from "./pages/reset-password"
import SignUp from "./pages/signup"
import Simulation from "./pages/simulation"
import SimulationDashboard from "./pages/simulation-dashboard"
import SimulationFinished from "./pages/simulation-finished"
import SimulationHome from "./pages/simulation-home"
import SimulationIntro from "./pages/simulation-intro"
import TableSample from "./pages/table-sample"
import TutorialIntro from "./pages/tutorial-intro"
import AuthGate from "./pages/auth-gate"
import Checkout from "./pages/checkout"
import AccountManagement from "./pages/account-management"
import Support from "./pages/support"
import OnboardingSetup from "./pages/onboarding-setup"
import OnboardingPassword from "./pages/onboarding-password"
import OnboardingSimple from "./pages/onboarding-simple"
import OnboardingMeasures from "./pages/onboarding-measures"
import OnboardingSuccess from "./pages/onboarding-success"
import LoginOtp from "./pages/login-otp"
import SignupRedirect from "./pages/signup-redirect"
import HashRouterHandler from "./components/hash-router-handler"
import RecruiterRoutes from "./components/RecruiterRoutes"
import RecruiterDashboard from "./pages/recruiter-dashboard"
import RecruiterDashboardWrapper from "./pages/recruiter/RecruiterDashboardWrapper"
import CollaborativeSimulation from "./pages/collaborative-simulation"
import CollaborativeSimulationSimple from "./pages/collaborative-simulation-simple"
import TeamSimulationSetup from "./pages/team-simulation-setup"
import TeamSimulationPurchase from "./pages/team-simulation-purchase"
import TeamSimulationLogin from "./pages/team-simulation-login"
import TeamSimulationComplete from "./pages/team-simulation-complete"
import SuperPowerComponent from "./pages/super-power"
import ProgressComponent from "./pages/progress"
import SuperAdminDashboard from "./pages/super-admin-dashboard"
import SuperAdminOverview from "./pages/super-admin/overview"
import LiveActivityPage from "./pages/super-admin/live-activity"
import PendingApprovalsPage from "./pages/super-admin/pending-approvals"
import PendingResultsApprovalsPage from "./pages/super-admin/pending-results-approvals"
import OrgReportsPage from "./pages/super-admin/org-reports"
import SimReportsPage from "./pages/super-admin/sim-reports"
import MeasuresPage from "./pages/super-admin/measures"
import OrgMeasuresPage from "./pages/super-admin/org-measures"
import DataExplorerPage from "./pages/super-admin/data-explorer"
import ScoreSetsPage from "./pages/super-admin/score-sets"
import AnalyticsPage from "./pages/super-admin/analytics"
import AllSimulationsPage from "./pages/super-admin/all-simulations"
import SimulationsLibraryPage from "./pages/super-admin/simulations-library"
import TeamSimulationsPage from "./pages/super-admin/team-simulations"
import ManageSimulationsPage from "./pages/super-admin/manage-simulations"
import SimTutorialBuilderPage from "./pages/super-admin/sim-tutorial-builder"
import SimulationsPage from "./pages/super-admin/simulations"
import WhoThinksSection from "./pages/recruiter-admin/WhoThinksSection"
import MicrolearningDashboard from "./pages/microlearning-dashboard"
import MeasureGuide from "./pages/measure-guide"
import SimulationCreator from "./pages/simulation-creator"
import SimulationReview from "./pages/simulation-review"
import TestSessionLogin from "./pages/test-session-login"
import TestSessionSimulation from "./pages/test-session-simulation"
import TestSessionReviewPage from "./pages/super-admin/test-session-review"
import OrgAnalyticsPage from "./pages/super-admin/org-analytics"
import RawDataPage from "./pages/super-admin/raw-data"

if (process.env.REACT_APP_LOGROCKET_ID) {
  LogRocket.init(process.env.REACT_APP_LOGROCKET_ID)
  if (window) {
    window.console.log = LogRocket.log
    window.console.error = LogRocket.error
    window.console.warn = LogRocket.warn
    window.console.info = LogRocket.info
    window.console.debug = LogRocket.debug
  }
}

function App() {
  const action = useNavigationType()
  const location = useLocation()
  const pathname = location.pathname

  // Initialize session checking
  useSessionCheck()

  const user = useTypedSelector((state) => state.authSlice.user)

  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0)
    }
  }, [action, pathname])

  // LogRocket session tracking
  useEffect(() => {
    if (process.env.REACT_APP_LOGROCKET_ID) {
      LogRocket.getSessionURL((sessionURL) => {
        console.debug("LogRocket session URL:", sessionURL)
      })

      if (user?.id) {
        LogRocket.identify(user.id.toString(), {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
          organization: user.organizationId,
          role: user.role,
        })
      }
    }
  }, [user?.email, user?.firstName, user?.id, user?.lastName, user?.organizationId, user?.role])

  useEffect(() => {
    let title = ""
    let metaDescription = ""

    switch (pathname) {
      case "/":
        title = ""
        metaDescription = ""
        break
      case "/login":
        title = ""
        metaDescription = ""
        break
      case "/dashboard/simulations":
        title = ""
        metaDescription = ""
        break
      case "/forgot-password":
        title = ""
        metaDescription = ""
        break
    }

    if (title) {
      document.title = title
    }

    if (metaDescription) {
      const metaDescriptionTag: HTMLMetaElement | null = document.querySelector(
        'head > meta[name="description"]',
      )
      if (metaDescriptionTag) {
        metaDescriptionTag.content = metaDescription
      }
    }
  }, [pathname])

  return (
    <Routes>
      {/* Public routes - authentication flows */}
      <Route path="/login" element={<LogIn />} />
      <Route path="/login/:compressedData" element={<LogIn />} />
      <Route path="/signup/:token" element={<SignupRedirect />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/createPassword/:id" element={<ResetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding/password" element={<OnboardingPassword />} />
      <Route path="/onboarding/setup" element={<OnboardingSetup />} />
      <Route path="/onboarding/simple" element={<OnboardingSimple />} />
      <Route path="/onboarding/measures" element={<OnboardingMeasures />} />
      <Route path="/onboarding/success" element={<OnboardingSuccess />} />
      <Route path="/login/otp" element={<LoginOtp />} />
      {process.env.REACT_APP_RUNNING_ENV !== "production" && (
        <Route path="/checkout" element={<Checkout />} />
      )}

      {/* Team Simulation Routes - No Auth Required for participant login */}
      <Route path="/team-simulations/:teamSimulationId/login" element={<TeamSimulationLogin />} />
      <Route path="/team-simulations/:teamSimulationId/simulation" element={<CollaborativeSimulationSimple />} />
      <Route path="/team-simulations/:teamSimulationId/simulation-full" element={<CollaborativeSimulation />} />
      <Route path="/team-simulations/:teamSimulationId/complete" element={<TeamSimulationComplete />} />

      {/* Test Session Routes (Public) */}
      <Route path="/test-sessions/:testSessionId/login" element={<TestSessionLogin />} />
      <Route path="/test-sessions/:testSessionId/simulation" element={<TestSessionSimulation />} />

      {/* Roots & Shoots Demo Routes - No Auth Required */}
      <Route path="/demo/roots-and-shoots" element={<CollaborativeSimulation />} />
      <Route path="/demo/roots-and-shoots/collaborative" element={<CollaborativeSimulation />} />
      <Route path="/demo/roots-and-shoots/:testId/:runId" element={<CollaborativeSimulation />} />


      {/* Protected routes wrapped in AuthGate */}
      <Route
        element={
          <AuthGate>
            <Outlet />
          </AuthGate>
        }>
        <Route path="/" element={<SimulationDashboard />} />
        <Route
          path="/dashboard/*"
          element={<Navigate to="/dashboard/simulations" replace />}
        />
        <Route
          path="/dashboard/simulations"
          element={<SimulationDashboard />}
        />
        <Route
          path="/dashboard/:testId/:runId/overView"
          element={<SuperPowerComponent />}
        />
        <Route
          path="/dashboard/:testId/:runId/progress"
          element={<ProgressComponent />}
        />
        <Route
          path="/simulations/complete/:testId/:runId"
          element={<SimulationFinished />}
        />
        <Route path="/table-sample" element={<TableSample />} />
        <Route path="/simulations/:testId/:runId" element={<Simulation />} />
        <Route path="/collaborative-simulation/:testId/:runId" element={<CollaborativeSimulation />} />
        <Route
          path="/simulations/:testId/:runId/tutorial/intro"
          element={<TutorialIntro />}
        />
        <Route
          path="/simulations/:testId/:runId/tutorial"
          element={<TutorialBase />}
        />
        <Route
          path="/simulations/:testId/:runId/intro"
          element={<SimulationIntro />}
        />
        <Route path="/simulations-home" element={<SimulationHome />} />
        <Route path="/account" element={<AccountManagement />} />
        <Route path="/support" element={<Support />} />

        {/* Team Simulation Admin Routes */}
        <Route path="/admin/team-simulations/purchase" element={<TeamSimulationPurchase />} />
        <Route path="/admin/team-simulations/setup" element={<TeamSimulationSetup />} />

        {/* Simulation Creator */}
        <Route path="/simulation-creator" element={<SimulationCreator />} />
        <Route path="/simulation-creator/:id" element={<SimulationCreator />} />
        <Route path="/simulations/:id/review" element={<SimulationReview />} />

        {/* AI Team Matcher (Super Admin) */}
        <Route path="/chatbot" element={<SiteAdminRoutes><WhoThinksSection showOrgSelector={true} standalone={true} /></SiteAdminRoutes>} />

        {/* Microlearning Protected Routes - My Development section */}
        {/* Only accessible by TeamMember (role 4), Admin (role 8), and SiteAdmin (role 1) */}
        {/* NOT accessible by SimulationUser/Candidates (role 5) */}
        <Route path="/microlearning" element={<TeamMemberRoutes><MicrolearningDashboard /></TeamMemberRoutes>} />
        <Route path="/microlearning/overview" element={<TeamMemberRoutes><MicrolearningDashboard /></TeamMemberRoutes>} />
        <Route path="/microlearning/journal" element={<TeamMemberRoutes><MicrolearningDashboard /></TeamMemberRoutes>} />
        <Route path="/microlearning/scenarios" element={<TeamMemberRoutes><MicrolearningDashboard /></TeamMemberRoutes>} />
        <Route path="/microlearning/settings" element={<TeamMemberRoutes><MicrolearningDashboard /></TeamMemberRoutes>} />
        <Route path="/measure-guide" element={<TeamMemberRoutes><MeasureGuide /></TeamMemberRoutes>} />
        <Route path="/measure-guide/:measureId" element={<MeasureGuide />} />

        {/* Site Admin routes */}
        <Route
          path="/dashboard/super-admin"
          element={
            <SiteAdminRoutes>
              <SuperAdminDashboard />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/overview"
          element={
            <SiteAdminRoutes>
              <SuperAdminOverview />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/live-activity"
          element={
            <SiteAdminRoutes>
              <LiveActivityPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/pending-approvals"
          element={
            <SiteAdminRoutes>
              <PendingApprovalsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/org-reports"
          element={
            <SiteAdminRoutes>
              <OrgReportsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/sim-reports"
          element={
            <SiteAdminRoutes>
              <SimReportsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/measures"
          element={
            <SiteAdminRoutes>
              <MeasuresPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/org-measures"
          element={
            <SiteAdminRoutes>
              <OrgMeasuresPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/data-explorer"
          element={
            <SiteAdminRoutes>
              <DataExplorerPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/score-sets"
          element={
            <SiteAdminRoutes>
              <ScoreSetsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/analytics"
          element={
            <SiteAdminRoutes>
              <AnalyticsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/all-simulations"
          element={
            <SiteAdminRoutes>
              <AllSimulationsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/simulations-library"
          element={
            <SiteAdminRoutes>
              <SimulationsLibraryPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/simulations"
          element={
            <SiteAdminRoutes>
              <SimulationsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/pending-results-approvals"
          element={
            <SiteAdminRoutes>
              <PendingResultsApprovalsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/team-simulations"
          element={
            <SiteAdminRoutes>
              <TeamSimulationsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/manage-simulations"
          element={
            <SiteAdminRoutes>
              <ManageSimulationsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/sims/:simId/tutorial"
          element={
            <SiteAdminRoutes>
              <SimTutorialBuilderPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/test-sessions/:testSessionId"
          element={
            <SiteAdminRoutes>
              <TestSessionReviewPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/org-analytics"
          element={
            <SiteAdminRoutes>
              <OrgAnalyticsPage />
            </SiteAdminRoutes>
          }
        />
        <Route
          path="/dashboard/super-admin/raw-data"
          element={
            <SiteAdminRoutes>
              <RawDataPage />
            </SiteAdminRoutes>
          }
        />

        {/* Admin/Recruiter routes wrapped in their respective route groups */}
        <Route
          path="/dashboard/recruiter-admin/*"
          element={
            <RecruiterAdminRoutes>
              <RecruiterAdminDashboard />
            </RecruiterAdminRoutes>
          }
        />

        {/* Recruiter routes */}
        <Route
          path="/dashboard/recruiter/*"
          element={
            <RecruiterRoutes>
              <Route path="welcome" element={<RecruiterDashboardWrapper />} />
              <Route path="composite" element={<RecruiterDashboard />} />
              <Route path="benchmark" element={<RecruiterDashboard />} />
              <Route path="performance" element={<RecruiterDashboard />} />
              <Route path="" element={<RecruiterDashboardWrapper />} />
            </RecruiterRoutes>
          }
        />
      </Route>

      {/* Catch-all route for unmatched paths */}
      <Route path="*" element={<HashRouterHandler />} />
    </Routes>
  )
}
export default App
